import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import type { AuthenticatorProps } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { usePostHog } from 'posthog-js/react';

import { Layout } from '@/components/layout/Layout';
import { Dashboard } from '@/pages/Dashboard';
import { Devices } from '@/pages/Devices';
import { DeviceDetail } from '@/pages/DeviceDetail';
import { PublicDeviceView } from '@/pages/PublicDeviceView';
import { Map } from '@/pages/Map';
import { Alerts } from '@/pages/Alerts';
import { Commands } from '@/pages/Commands';
import { Settings } from '@/pages/Settings';
import { Analytics } from '@/pages/Analytics';
import { PreferencesProvider } from '@/contexts/PreferencesContext';
import { initializeApi } from '@/api/client';
import { useActiveAlerts } from '@/hooks/useAlerts';
import { usePostHogIdentify } from '@/hooks/useAuth';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

interface AppConfig {
  apiUrl: string;
  region: string;
  userPoolId: string;
  userPoolClientId: string;
  mapboxToken?: string;
}

// Custom Authenticator components for branding — defined at module scope so the
// object reference is stable across App re-renders.
const authenticatorComponents: AuthenticatorProps['components'] = {
  Header() {
    return (
      <div className="flex flex-col items-center pt-8 pb-4">
        <img src="/songbird-logo.svg" alt="Songbird" className="h-16 w-16 mb-3" />
        <h1 className="text-2xl font-bold text-foreground">Songbird</h1>
        <p className="text-sm text-muted-foreground">Fleet Management Dashboard</p>
      </div>
    );
  },
  Footer() {
    return (
      <div className="text-center py-4 text-xs text-muted-foreground">
        Powered by Blues Inc.
      </div>
    );
  },
};

// Custom form fields for sign-up and sign-in — defined at module scope so the
// object reference is stable across App re-renders.
const authenticatorFormFields: AuthenticatorProps['formFields'] = {
  signIn: {
    username: {
      label: 'Username (email)',
      placeholder: 'Enter your email',
    },
  },
  signUp: {
    username: {
      label: 'Username (email)',
      placeholder: 'Enter your email',
      order: 1,
    },
    name: {
      label: 'Full Name',
      placeholder: 'Enter your full name',
      order: 2,
      isRequired: true,
    },
    password: {
      order: 3,
    },
    confirm_password: {
      order: 4,
    },
  },
};

// Component to track page views with PostHog — defined at module scope so React
// sees a stable component type and never unnecessarily remounts it.
function PageViewTracker() {
  const location = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    posthog?.capture('$pageview', {
      $current_url: window.location.href,
    });
  }, [location.pathname, posthog]);

  return null;
}

// Wrapper component to use hooks inside QueryClientProvider — defined at module
// scope so React sees a stable component type and never remounts the subtree
// when App re-renders (e.g. on fleet change).
interface AppLayoutProps {
  user?: { username: string; email: string };
  signOut?: () => void;
  selectedFleet: string;
  onFleetChange: (fleetUid: string) => void;
}

function AppLayout({ user, signOut, selectedFleet, onFleetChange }: AppLayoutProps) {
  const { data: alertsData } = useActiveAlerts();
  const alertCount = alertsData?.active_count || 0;

  // Identify user to PostHog on login
  usePostHogIdentify();

  return (
    <Layout
      user={user}
      alertCount={alertCount}
      selectedFleet={selectedFleet}
      fleets={[]}
      onFleetChange={onFleetChange}
      onSignOut={signOut}
    />
  );
}

function App() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [selectedFleet, setSelectedFleet] = useState<string>('all');

  // Load config on mount
  useEffect(() => {
    async function loadConfig() {
      try {
        const response = await fetch('/config.json');
        if (!response.ok) {
          throw new Error('Failed to load config');
        }
        const cfg: AppConfig = await response.json();

        // Initialize API
        initializeApi(cfg.apiUrl);

        // Configure Amplify
        Amplify.configure({
          Auth: {
            Cognito: {
              userPoolId: cfg.userPoolId,
              userPoolClientId: cfg.userPoolClientId,
            },
          },
        });

        setConfig(cfg);
      } catch (err) {
        console.error('Config load error:', err);
        setConfigError('Failed to load application configuration');
      }
    }

    loadConfig();
  }, []);

  if (configError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-500 mb-2">Configuration Error</h1>
          <p className="text-muted-foreground">{configError}</p>
          <p className="text-sm mt-4">
            Make sure config.json exists in the public directory.
          </p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <img
            src="/songbird-logo.svg"
            alt="Songbird"
            className="h-16 w-16 mx-auto mb-4 animate-pulse"
          />
          <p className="text-muted-foreground">Loading Songbird Dashboard...</p>
        </div>
      </div>
    );
  }

  const mapboxToken = config.mapboxToken || import.meta.env.VITE_MAPBOX_TOKEN || '';

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PageViewTracker />
        <Routes>
          {/* Public routes - no authentication required */}
          <Route
            path="/public/device/:serialNumber"
            element={<PublicDeviceView mapboxToken={mapboxToken} />}
          />

          {/* Authenticated routes */}
          <Route
            path="/*"
            element={
              <Authenticator components={authenticatorComponents} formFields={authenticatorFormFields}>
                {({ signOut, user }) => (
                  <PreferencesProvider>
                    <Routes>
                      <Route
                        element={
                          <AppLayout
                            user={user ? { username: user.username || '', email: user.signInDetails?.loginId || '' } : undefined}
                            signOut={signOut}
                            selectedFleet={selectedFleet}
                            onFleetChange={setSelectedFleet}
                          />
                        }
                      >
                        <Route
                          index
                          element={
                            <Dashboard
                              mapboxToken={mapboxToken}
                              selectedFleet={selectedFleet}
                            />
                          }
                        />
                        <Route
                          path="/devices"
                          element={<Devices />}
                        />
                        <Route
                          path="/devices/:serialNumber"
                          element={<DeviceDetail mapboxToken={mapboxToken} />}
                        />
                        <Route
                          path="/map"
                          element={
                            <Map
                              mapboxToken={mapboxToken}
                              selectedFleet={selectedFleet}
                            />
                          }
                        />
                        <Route
                          path="/alerts"
                          element={<Alerts />}
                        />
                        <Route
                          path="/commands"
                          element={<Commands />}
                        />
                        <Route
                          path="/analytics"
                          element={<Analytics mapboxToken={mapboxToken} />}
                        />
                        <Route
                          path="/settings"
                          element={<Settings />}
                        />
                      </Route>
                    </Routes>
                  </PreferencesProvider>
                )}
              </Authenticator>
            }
          />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
